/**
 * The card's own menu, in the same overflow idiom as the day header's: one icon
 * button, one action, and nothing priced or fetched by opening it. The two
 * actions are real server changes — a cancel with the branch's own reason, and
 * the product's own way of removing a block — undone by Reset database.
 *
 * A card that cannot offer its action carries no menu at all rather than a
 * disabled item: a visit already delivered, one on a day already spent, and a
 * change the server has not confirmed yet are simply not asked about.
 *
 * **Mark confirmed** is the one exception, and the one item that is disabled
 * rather than absent. It is a store write against the day the *server* has, and
 * My day stages a re-time until **Save**, so on a staged card it would confirm
 * one position and immediately re-read the other. It says *Save the day first*
 * instead.
 *
 * One component for the week column and for My day, so the same card offers the
 * same words wherever it is drawn.
 */
import { useState } from "react";
import { IconButton, ListItemText, Menu, MenuItem } from "@mui/material";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import { useBranchClock } from "../../components/branchClock";
import type { WeekCard } from "../../engine/myWeek";

export function CardMenu({
  card,
  dayLabel,
  timeZone,
  staged,
  onCancelVisit,
  onRemoveBlock,
  onMarkConfirmed,
}: {
  card: WeekCard;
  dayLabel: string;
  /** The branch's zone: the card is named by the hour the patient has, not the laptop's. */
  timeZone: string;
  /** My day only: this card carries a re-time that is still waiting for **Save**. */
  staged?: boolean;
  onCancelVisit: (card: WeekCard) => void;
  onRemoveBlock: (card: WeekCard) => void;
  onMarkConfirmed?: (card: WeekCard) => void;
}) {
  const clock = useBranchClock(timeZone);
  const confirmBlocked = !!staged || card.pending;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const close = () => setAnchorEl(null);
  return (
    <>
      <IconButton size="small" aria-label={`Actions for ${card.title} ${dayLabel} ${clock.wall(card.start)}`} onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.25 }}>
        <MoreHoriz sx={{ fontSize: 16 }} />
      </IconButton>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={close} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        {card.kind === "block" ? (
          <MenuItem
            onClick={() => {
              close();
              onRemoveBlock(card);
            }}
          >
            <ListItemText>Remove block</ListItemText>
          </MenuItem>
        ) : (
          card.removable && (
            <MenuItem
              onClick={() => {
                close();
                onCancelVisit(card);
              }}
            >
              <ListItemText>Cancel visit</ListItemText>
            </MenuItem>
          )
        )}
        {card.markConfirmable && onMarkConfirmed && (
          <MenuItem
            disabled={confirmBlocked}
            onClick={() => {
              close();
              onMarkConfirmed(card);
            }}
          >
            <ListItemText primary="Mark confirmed" secondary={confirmBlocked ? "Save the day first" : undefined} />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
